import {Box, IconButton, InputAdornment, TextField as MuiTextField} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import {useState, useRef} from "react";
import {FiX} from "react-icons/fi";

const MAX_DIGITS = 8;
const FULL_MASK = "DD/MM/YYYY";

const formatDateDisplay = (digits) => {
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
};

const convertDDMMYYYYtoYYYYMMDD = (ddmmyyyy) => {
  if (!ddmmyyyy || ddmmyyyy.length !== 10) return "";
  const [dd, mm, yyyy] = ddmmyyyy.split("/");
  return `${yyyy}-${mm}-${dd}`;
};

const convertYYYYMMDDtoDigits = (yyyymmdd) => {
  if (!yyyymmdd || yyyymmdd.length !== 10) return "";
  const [yyyy, mm, dd] = yyyymmdd.split("-");
  return `${dd}${mm}${yyyy}`;
};

// El cursor siempre se mantiene al final, evita el reordenamiento de editar en medio de la fecha.
const moveCaretToEnd = (input) => {
  requestAnimationFrame(() => {
    if (input) {
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }
  });
};

const MaskedDateInput = ({label, digits, setDigits, otherDigits, isFrom, emit, onEnter, size, inputRef}) => {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onEnter?.();
      return;
    }
    if (["Tab", "Shift", "Control", "Meta", "Alt", "CapsLock"].includes(e.key)) {
      return;
    }
    if ((e.ctrlKey || e.metaKey) && ["v", "V", "c", "C", "a", "A", "x", "X"].includes(e.key)) {
      return;
    }

    e.preventDefault();

    let newDigits = digits;
    if (e.key >= "0" && e.key <= "9") {
      if (digits.length >= MAX_DIGITS) return;
      newDigits = digits + e.key;
    } else if (e.key === "Backspace" || e.key === "Delete") {
      if (digits.length === 0) return;
      newDigits = digits.slice(0, -1);
    } else {
      return;
    }

    setDigits(newDigits);
    emit(isFrom ? newDigits : otherDigits, isFrom ? otherDigits : newDigits);
    moveCaretToEnd(inputRef.current);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, MAX_DIGITS);
    if (!pasted) return;
    setDigits(pasted);
    emit(isFrom ? pasted : otherDigits, isFrom ? otherDigits : pasted);
    moveCaretToEnd(inputRef.current);
  };

  const handleClear = () => {
    setDigits("");
    emit(isFrom ? "" : otherDigits, isFrom ? otherDigits : "");
    inputRef.current?.focus();
  };

  const formatted = formatDateDisplay(digits);
  const remainingMask = FULL_MASK.slice(formatted.length);

  return (
    <Box sx={{position: "relative", width: 170}}>
      <MuiTextField
        inputRef={inputRef}
        label={label}
        size={size}
        value={formatted}
        onChange={() => {}}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => {
          setFocused(true);
          moveCaretToEnd(inputRef.current);
        }}
        onClick={() => moveCaretToEnd(inputRef.current)}
        onBlur={() => setFocused(false)}
        InputLabelProps={{shrink: focused || !!digits}}
        InputProps={{
          endAdornment: digits ? (
            <InputAdornment position="end">
              <IconButton size="small" tabIndex={-1} onClick={handleClear} edge="end">
                <FiX size={14} />
              </IconButton>
            </InputAdornment>
          ) : null
        }}
        fullWidth
      />
      {focused && remainingMask && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            pointerEvents: "none",
            pl: "14px",
            fontSize: "1rem",
            whiteSpace: "pre"
          }}
        >
          <span style={{visibility: "hidden"}}>{formatted}</span>
          <span style={{color: alpha(theme.palette.text.secondary, 0.7)}}>{remainingMask}</span>
        </Box>
      )}
    </Box>
  );
};

const DateRangeField = ({config, value, onChange, onEnter, size}) => {
  const [from = "", to = ""] = (value ?? "").split(",");
  const [fromDigits, setFromDigits] = useState(convertYYYYMMDDtoDigits(from));
  const [toDigits, setToDigits] = useState(convertYYYYMMDDtoDigits(to));
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const emit = (newFromDigits, newToDigits) => {
    const fromFormatted = formatDateDisplay(newFromDigits);
    const toFormatted = formatDateDisplay(newToDigits);
    const fromValue = fromFormatted.length === 10 ? convertDDMMYYYYtoYYYYMMDD(fromFormatted) : "";
    const toValue = toFormatted.length === 10 ? convertDDMMYYYYtoYYYYMMDD(toFormatted) : "";
    onChange(!fromValue && !toValue ? "" : `${fromValue},${toValue}`);
  };

  return (
    <Box sx={{display: "flex", gap: 0.5, alignItems: "center"}}>
      <MaskedDateInput
        label="Fecha desde"
        digits={fromDigits}
        setDigits={setFromDigits}
        otherDigits={toDigits}
        isFrom
        emit={emit}
        onEnter={onEnter}
        size={size}
        inputRef={fromRef}
      />
      <MaskedDateInput
        label="Fecha hasta"
        digits={toDigits}
        setDigits={setToDigits}
        otherDigits={fromDigits}
        isFrom={false}
        emit={emit}
        onEnter={onEnter}
        size={size}
        inputRef={toRef}
      />
    </Box>
  );
};

export default DateRangeField;
